/* eslint max-lines: ["error", { max: 280 }] -- applies to entire file */
/* eslint max-lines-per-function: ["error", { max: 165 }] -- applies to entire file */
/* eslint max-statements: ["error", { max: 25 }] -- applies to entire file */
import { useCallback, useEffect, useMemo, useState } from "react";

import isEmpty from "lodash/isEmpty";

import { AnalyticsComponent } from "@/constants/analytics/Analytics";
import {
  ScheduleAnalyticsEvent,
  ScheduleAnalyticsPageView,
  ScheduleAnalyticsSources,
} from "@/constants/analytics/Scheduling";
import { TabSources } from "@/constants/analytics/Tabs";
import { ModalPath } from "@/constants/Navigation";
import { ScheduleTabSubDeepLinks } from "@/constants/Schedule";
import { FABTestID, ScheduleTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import useAppointmentSubDeepLink from "@/hooks/deepLinking/subDeepLinks/useAppointmentSubDeepLink";
import useGroupClassEventSubDeepLink from "@/hooks/deepLinking/subDeepLinks/useGroupClassEventSubDeepLink";
import useSubDeepLink from "@/hooks/deepLinking/useSubDeepLink";
import useBookingAnalytics from "@/hooks/useBookingAnalytics";
import useScheduleEvents from "@/hooks/useScheduleEvents";
import useShouldShowBookAppointment from "@/hooks/useShouldShowBookAppointment";
import { useAnalytics } from "@/services/Analytics";
import { useGetFullProfileQuery } from "@/store/api/user";
import { useAppSelector } from "@/store/hooks";
import {
  selectAppUserExternalId,
  selectAppUserIsPatient,
  selectAppUserIsSupport,
  useAppUserProxyPatientsSelector,
} from "@/store/reducers/appUser";
import EmptyListBlurb from "@/ui/components/schedule/emptyScheduleEventsList/EmptyListBlurb";
import EmptyListCard from "@/ui/components/schedule/emptyScheduleEventsList/EmptyListCard";
import ScheduleEventCard from "@/ui/components/schedule/ScheduleEventCard";
import ScheduleEventsListTitle from "@/ui/components/schedule/ScheduleEventsListTitle";
import { FAIconName, FloatingButton, SectionHeader } from "@/ui/core";
import {
  Box,
  ScreenContainer,
  SectionList,
  VStack,
  WebContentConstraints,
} from "@/ui/layout";
import { ListDimensions } from "@/ui/layout/list/ListComponents";
import { isWeb } from "@/utils/platform.util";
import {
  getAppointmentDetailResponseFromScheduleEvent,
  getScheduleEventProps,
} from "@/utils/schedule.util";
import { getTabSelectorOptions } from "@/utils/user.util";

const renderScheduleEventListItem = ({
  selectedUserId,
}: {
  selectedUserId: string;
}) => {
  // eslint-disable-next-line react/display-name
  return ({ item }: { item: ScheduledEventResponseItem }) => {
    const eventProps = getScheduleEventProps({
      event: item,
      isGroupClassEvent: item.isGroupClassEvent,
    });
    // eslint-disable-next-line no-restricted-syntax -- legitimate use case for a type guard
    const {
      attendees,
      description,
      endDate,
      externalId,
      frequency,
      groupClassExternalId,
      groupSectionId,
      hasCoreTag,
      iconName,
      isGroupClassEvent,
      isPhoneCallEvent,
      isRecurring,
      isSensitive,
      selectedUserId: eventPatientId,
      startDate,
      title,
      typeGroup,
    } = eventProps;

    const appointmentMetaData =
      getAppointmentDetailResponseFromScheduleEvent(item);

    return (
      <ScheduleEventCard
        appointmentMetaData={appointmentMetaData}
        attendees={attendees}
        description={description}
        endDate={endDate}
        externalId={externalId}
        frequency={frequency}
        groupClassExternalId={groupClassExternalId}
        groupSectionId={groupSectionId}
        hasCoreTag={hasCoreTag}
        iconName={iconName}
        isGroupClassEvent={isGroupClassEvent}
        isPhoneCallEvent={isPhoneCallEvent}
        isRecurring={isRecurring}
        isSensitive={isSensitive}
        key={`${externalId}-${startDate}`}
        patientId={eventPatientId}
        selectedUserId={selectedUserId}
        startDate={startDate}
        title={title}
        typeGroup={typeGroup}
      />
    );
  };
};

const Tab = () => {
  const { trackPageView, trackUserSelectorEvent } = useAnalytics();
  const { trackStartBooking } = useBookingAnalytics();

  const appUserId = useAppSelector(selectAppUserExternalId);
  const isAppUserPatient = useAppSelector(selectAppUserIsPatient);
  const isAppUserSupport = useAppSelector(selectAppUserIsSupport);

  const proxyPatients = useAppUserProxyPatientsSelector();
  const [selectedUserId, setSelectedUserId] = useState<string>(appUserId);
  const { isLoading, refetch, scheduleEvents } = useScheduleEvents({
    isPastEvents: false,
    isPatient: isAppUserPatient,
    patientExternalId: selectedUserId,
  });
  const [isManualRefreshing, setIsManualRefreshing] = useState<boolean>(false);

  const handleRefresh = useCallback(async () => {
    setIsManualRefreshing(true);
    await refetch();
    setIsManualRefreshing(false);
  }, [refetch]);

  const userTabs = useMemo(
    () =>
      getTabSelectorOptions({
        appUserCount: 0,
        appUserId,
        isAppUserPatient,
        patients: proxyPatients,
        shouldIncludeSelf: true,
      }) || [],
    [appUserId, isAppUserPatient, proxyPatients],
  );

  const { data: fullProfileData } = useGetFullProfileQuery();
  const selectedUniversalStatusCode = useMemo(
    () =>
      selectedUserId === appUserId
        ? fullProfileData?.universalStatusCode
        : userTabs.find((user) => user.userId === selectedUserId)
            ?.universalStatusCode,
    [appUserId, fullProfileData, selectedUserId, userTabs],
  );

  const handleUserChange = (userId: string) => {
    trackUserSelectorEvent({
      component: AnalyticsComponent.Tab,
      selectedUser: userId,
      source: ScheduleAnalyticsSources.ScheduleScreen,
    });

    setSelectedUserId(userId);
  };

  useEffect(() => {
    trackPageView(ScheduleAnalyticsPageView.ScheduleScreen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBookAppointment = useCallback(() => {
    router.navigate(ModalPath.BookAppointment);
    trackStartBooking(TabSources.Schedule);
  }, [trackStartBooking]);

  const shouldShowBookAppointment = useShouldShowBookAppointment({
    appUserId,
    isAppUserPatient,
    selectedUniversalStatusCode,
    selectedUserId,
  });

  useSubDeepLink({
    analyticsEvent: ScheduleAnalyticsEvent.BookAppointmentOpenedFromDeepLink,
    analyticsProps: {
      component: AnalyticsComponent.Tab,
      source: ScheduleAnalyticsSources.ScheduleScreen,
    },
    onOpen: handleBookAppointment,
    shouldHandle: shouldShowBookAppointment,
    targetValue: ScheduleTabSubDeepLinks.BookAppointment,
  });
  useAppointmentSubDeepLink(ScheduleTabSubDeepLinks.Appointment);
  useAppointmentSubDeepLink(ScheduleTabSubDeepLinks.EditAppointment);
  useGroupClassEventSubDeepLink();

  // useCallback, not useMemo, due to SectionList.EmptyListComponent expecting a callback to render a component
  const emptyListCard = useCallback(
    () => (
      <EmptyListCard
        handleBookAppointment={handleBookAppointment}
        shouldShowBookAppointment={shouldShowBookAppointment}
      />
    ),
    [handleBookAppointment, shouldShowBookAppointment],
  );

  const isListEmpty = isEmpty(scheduleEvents) && !isLoading;
  const paddingBottom = isListEmpty ? 0 : ListDimensions.FABBottomOffset;

  return (
    <ScreenContainer>
      <Box style={WebContentConstraints}>
        <SectionHeader
          appUserId={appUserId}
          isAppUserSupport={isAppUserSupport}
          onUserChange={handleUserChange}
          testID={ScheduleTestID.Schedule}
          title="Schedule"
          userTabs={userTabs}
        />
      </Box>
      <VStack flex={isWeb() ? 1 : undefined} gap={36}>
        <SectionList
          contentContainerPaddingBottom={paddingBottom}
          emptyListComponent={emptyListCard}
          isDataLoading={isLoading}
          isRefreshControlLoading={isManualRefreshing}
          keyExtractor={(item) =>
            "groupClassEventExternalId" in item
              ? item.groupClassEventExternalId
              : item.appointmentExternalId
          }
          onRefresh={() => void handleRefresh()}
          renderItem={renderScheduleEventListItem({
            selectedUserId,
          })}
          renderSectionHeader={({ section }) => (
            <ScheduleEventsListTitle title={section.title} />
          )}
          sections={scheduleEvents}
          sectionSeparatorSize="md"
          separatorType="space"
          shouldShowRefreshControl
          testID={ScheduleTestID.Schedule}
        />
        {isListEmpty && (
          <EmptyListBlurb
            universalStatusCode={selectedUniversalStatusCode}
            userId={selectedUserId}
          />
        )}
      </VStack>
      {shouldShowBookAppointment && (
        <FloatingButton
          accessibilityLabel="Book an appointment"
          leftIcon={FAIconName.Calendar}
          onPress={handleBookAppointment}
          testID={FABTestID.BookAppointment}
          title="Book an appointment"
        />
      )}
    </ScreenContainer>
  );
};

export default Tab;
